import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  UseQueryResult,
} from '@tanstack/react-query'
import { ROUTES } from '../../../../constants'
import { IPaginatedData } from 'models/shared.models'
import {
  IInvoice,
  CreditNote,
  CreditNoteFiles,
  IInvoiceDraft,
  PaymentComplement,
  PaymentComplementFiles,
} from 'models/invoices.models'
import {
  selectActiveWorkspace,
  selectInvoicesFilter,
} from 'lib/helpers/selectors'
import useDataGridServerPagination, {
  IUseDataGridServerPagination,
} from 'hooks/Shared/useDataGridServerPagination'
import useCreditNoteList from 'hooks/Invoices/CFDIs/useCreditNoteList'
import useDownloadCancelNoticeFile from 'hooks/Invoices/CFDIs/useDownloadCancelNoticeFile'
import useDownloadCreditNoteFiles from 'hooks/Invoices/CFDIs/useDownloadCreditNoteFiles'
import useDownloadPaymentComplementFiles from 'hooks/Invoices/CFDIs/useDownloadPaymentComplementFiles'
import usePaymentComplementList from 'hooks/Invoices/CFDIs/usePaymentComplementList'
import { useAppSelector } from 'store/hooks'
import { IApiError } from 'services/Shared/shared.models'
import { getCFDIDrafts, getCfdiList } from 'services/Invoices/invoices.services'
import { ICFDIsContainerProps } from './CFDIsContainer'
import { ICFDIsContainerState } from './useCFDIsContainer'
import { useNavigate } from 'react-router-dom'

interface IUseCFDIsContainerQueriesProps
  extends ICFDIsContainerProps,
    Pick<
      ICFDIsContainerState,
      | 'activeInvoice'
      | 'relatedDocsDialogOpen'
      | 'selectedCreditNote'
      | 'selectedPayComplement'
    > {
  isCFDIsTab: boolean
  updateState: (update: (draft: ICFDIsContainerState) => void) => void
}

export interface IUseCFDIsContainerQueries {
  refetchInvoices: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<
    QueryObserverResult<IPaginatedData<IInvoice | IInvoiceDraft>, IApiError>
  >
  CFDIsPaginatedData: Omit<
    IUseDataGridServerPagination<IInvoice | IInvoiceDraft>,
    'refetch'
  >
  downloadInvoiceCancelNoticeQuery: UseQueryResult<string, unknown>
  payComplementListQuery: UseQueryResult<PaymentComplement[], any>
  downloadPayComplementQuery: UseQueryResult<PaymentComplementFiles, unknown>
  creditNoteListQuery: UseQueryResult<CreditNote[], any>
  downloadCreditNoteQuery: UseQueryResult<CreditNoteFiles, unknown>
}

const useInvoicesQueries = ({
  activeInvoice,
  emisor,
  relatedDocsDialogOpen,
  selectedCreditNote,
  selectedPayComplement,
  isCFDIsTab,
  onRedirect,
  updateState,
}: IUseCFDIsContainerQueriesProps): IUseCFDIsContainerQueries => {
  const navigate = useNavigate()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)
  const filters = useAppSelector(selectInvoicesFilter)

  const { refetch: refetchInvoices, ...CFDIsPaginatedData } =
    useDataGridServerPagination<IInvoice | IInvoiceDraft, any>({
      activeWorkspace,
      fetchFunction: isCFDIsTab ? getCfdiList : getCFDIDrafts,
      errorMessage: isCFDIsTab
        ? 'Hubo un error al obtener la información de los CFDIs.'
        : 'Hubo un error al obtener la información de las prefacturas.',
      withFilters: true,
      filters: {
        rfc: emisor?.rfc,
        search: filters?.search,
        advance: filters?.advance,
        type: filters?.type?.value,
        status: filters?.status?.value,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        receptor: filters?.receptor,
      },
      refreshOn: isCFDIsTab,
    })

  const typedInvoice = activeInvoice as IInvoice

  const creditNoteListQuery = useCreditNoteList(activeWorkspace, typedInvoice, {
    enabled: relatedDocsDialogOpen,
    onError: () => navigate(ROUTES.HOME, { replace: true }),
  })

  const downloadCreditNoteQuery = useDownloadCreditNoteFiles(
    activeWorkspace,
    typedInvoice,
    selectedCreditNote
  )

  const downloadInvoiceCancelNoticeQuery = useDownloadCancelNoticeFile(
    activeWorkspace,
    typedInvoice,
    {
      onSuccess: () =>
        updateState(draft => {
          draft.seeMoreAnchor = null
        }),
    }
  )

  const downloadPayComplementQuery = useDownloadPaymentComplementFiles(
    activeWorkspace,
    typedInvoice,
    selectedPayComplement
  )

  const payComplementListQuery = usePaymentComplementList(
    activeWorkspace,
    typedInvoice,
    {
      enabled: relatedDocsDialogOpen,
      onError: () => navigate(ROUTES.HOME, { replace: true }),
    }
  )

  return {
    CFDIsPaginatedData,
    creditNoteListQuery,
    downloadCreditNoteQuery,
    downloadInvoiceCancelNoticeQuery,
    downloadPayComplementQuery,
    payComplementListQuery,
    refetchInvoices,
  }
}

export default useInvoicesQueries
